import { useState, type ReactElement } from "react";
import { Textarea } from "../atoms/index.js";
import { Tabs, TabPanel } from "../molecules/index.js";
import { MarkdownContent } from "../widgets/markdownText/MarkdownContent.js";
import type { ProposalReviewField } from "./proposal-review.types.js";

/** Preview always reads the same draft as the editor; switching never changes it. */
export function ProposalReviewTextArea({
  field,
  value,
  onChange,
  readOnly,
  controlId,
  errorId,
}: {
  field: ProposalReviewField;
  value: string;
  onChange: (value: unknown) => void;
  readOnly: boolean;
  controlId: string;
  errorId: string | undefined;
}): ReactElement {
  const [mode, setMode] = useState(readOnly ? "preview" : "edit");
  return (
    <Tabs
      aria-label={`${field.label} view`}
      value={mode}
      onValueChange={setMode}
      items={[
        { value: "edit", label: readOnly ? "Source" : "Edit" },
        { value: "preview", label: "Preview" },
      ]}
    >
      <TabPanel value="edit">
        {readOnly ? (
          <p
            id={controlId}
            className="whitespace-pre-wrap break-words text-dense-1"
          >
            {value || "—"}
          </p>
        ) : (
          <Textarea
            id={controlId}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            {...(field.constraints?.maxLength !== undefined
              ? { maxLength: field.constraints.maxLength, showCount: true }
              : {})}
            rows={7}
            aria-invalid={field.error !== undefined || undefined}
            aria-describedby={errorId}
          />
        )}
      </TabPanel>
      <TabPanel value="preview">
        <div className="min-h-40 rounded-md border border-pb-border bg-pb-shell/60 px-3 py-2">
          {containsHtmlSource(value) ? (
            <div>
              <p className="mb-2 text-dense-2 text-pb-text-muted">
                Source preview (HTML is shown as text).
              </p>
              <p className="whitespace-pre-wrap break-words text-dense-1">
                {value}
              </p>
            </div>
          ) : value ? (
            <MarkdownContent markdown={value} tableLayout="scroll" />
          ) : (
            <p className="text-dense-1 text-pb-text-muted">
              Nothing to preview.
            </p>
          )}
        </div>
      </TabPanel>
    </Tabs>
  );
}

/** Keep URL/email autolinks in Markdown; actual tags use an escaped source preview. */
function containsHtmlSource(value: string): boolean {
  const withoutAutolinks = value.replace(
    /<(?:[a-z][a-z0-9+.-]{1,31}:[^\s<>]*|[^\s<>@]+@[^\s<>@]+)>/gi,
    "",
  );
  return /<\/?[a-z][^>]*>/i.test(withoutAutolinks);
}
